interface InputCallbacks {
  onLetter: (letter: string) => void;
}

interface InputManagerOptions extends InputCallbacks {
  stage: HTMLElement;
  keyboardHost: HTMLElement;
  doc?: Document;
}

const KEYBOARD_ROWS = ['qwertyuiop', 'asdfghjkl', 'zxcvbnm'];

export class InputManager {
  private readonly stage: HTMLElement;
  private readonly keyboardHost: HTMLElement;
  private readonly doc: Document;
  private readonly onLetter: (letter: string) => void;
  private readonly keyboard: HTMLElement;
  private rafUpdateId: number | null = null;

  constructor(options: InputManagerOptions) {
    this.stage = options.stage;
    this.keyboardHost = options.keyboardHost;
    this.doc = options.doc ?? options.stage.ownerDocument ?? document;
    this.onLetter = options.onLetter;

    this.stage.setAttribute('tabindex', '0');
    this.stage.setAttribute('role', 'textbox');
    this.stage.setAttribute('aria-label', 'Typing focus area');
    this.stage.setAttribute('aria-describedby', 'difficulty-note');

    this.stage.addEventListener('pointerdown', this.handlePointerDown, { passive: true });

    this.doc.addEventListener('keydown', this.handleKeyDown);

    this.keyboard = this.createVirtualKeyboard();
    this.keyboard.setAttribute('role', 'group');
    this.keyboard.setAttribute('aria-label', 'On-screen keyboard');
    this.keyboard.setAttribute('aria-hidden', 'true');
    this.keyboardHost.append(this.keyboard);
    this.updateKeyboardVisibility();
    this.doc.defaultView?.addEventListener('resize', this.scheduleVisibilityUpdate);
    this.watchMedia('(pointer: coarse)');
    this.watchMedia('(any-hover: none)');
  }

  dispose(): void {
    this.stage.removeEventListener('pointerdown', this.handlePointerDown);
    this.doc.removeEventListener('keydown', this.handleKeyDown);
    this.doc.defaultView?.removeEventListener('resize', this.scheduleVisibilityUpdate);
    this.cancelVisibilityUpdate();
    this.keyboard.remove();
  }

  private handlePointerDown = (event: PointerEvent) => {
    if (event.pointerType !== 'touch') {
      this.focusStage();
    }
  };

  private focusStage = () => {
    this.stage.focus({ preventScroll: true });
  };

  private handleKeyDown = (event: KeyboardEvent) => {
    const target = event.target as HTMLElement | null;
    if (target && this.isFormElement(target)) {
      return;
    }

    if (event.key.length === 1) {
      const lower = event.key.toLowerCase();
      if (lower >= 'a' && lower <= 'z') {
        event.preventDefault();
        this.onLetter(lower);
      }
    }
  };

  private createVirtualKeyboard(): HTMLElement {
    const keyboard = this.doc.createElement('div');
    keyboard.className = 'virtual-keyboard';

    const handleKey = (letter: string) => {
      this.onLetter(letter);
      this.focusStage();
    };

    for (const row of KEYBOARD_ROWS) {
      const rowEl = this.doc.createElement('div');
      rowEl.className = 'virtual-keyboard__row';
      for (const ch of row) {
        const button = this.doc.createElement('button');
        button.type = 'button';
        button.className = 'virtual-keyboard__key';
        button.textContent = ch.toUpperCase();
        button.setAttribute('aria-label', `Type ${ch.toUpperCase()}`);
        button.setAttribute('title', `Type ${ch.toUpperCase()}`);
        button.addEventListener('click', () => handleKey(ch));
        button.addEventListener('pointerdown', (event) => event.preventDefault());
        rowEl.append(button);
      }
      keyboard.append(rowEl);
    }

    return keyboard;
  }

  private isFormElement(element: HTMLElement): boolean {
    const tag = (element.tagName || '').toLowerCase();
    if (!tag) {
      return false;
    }
    return tag === 'input' || tag === 'select' || tag === 'textarea' || element.isContentEditable;
  }

  private watchMedia(query: string): void {
    const media = this.doc.defaultView?.matchMedia?.(query);
    if (!media) {
      return;
    }
    const listener = () => this.updateKeyboardVisibility();
    if (typeof media.addEventListener === 'function') {
      media.addEventListener('change', listener);
    } else if (typeof media.addListener === 'function') {
      media.addListener(listener);
    }
  }

  private scheduleVisibilityUpdate = () => {
    if (this.rafUpdateId !== null) {
      return;
    }
    this.rafUpdateId = this.doc.defaultView?.requestAnimationFrame(() => {
      this.rafUpdateId = null;
      this.updateKeyboardVisibility();
    }) ?? null;
  };

  private cancelVisibilityUpdate(): void {
    if (this.rafUpdateId !== null && this.doc.defaultView) {
      this.doc.defaultView.cancelAnimationFrame(this.rafUpdateId);
      this.rafUpdateId = null;
    }
  }

  private updateKeyboardVisibility(): void {
    const win = this.doc.defaultView;
    let visible = false;
    if (win?.matchMedia?.('(pointer: coarse)').matches) {
      visible = true;
    } else if (win?.matchMedia?.('(any-hover: none)').matches) {
      visible = true;
    } else if (navigator.maxTouchPoints > 0) {
      visible = true;
    }

    this.keyboard.classList.toggle('virtual-keyboard--visible', visible);
    this.keyboard.setAttribute('aria-hidden', String(!visible));
  }
}
